/**
 * SupabaseSessionSync.jsx — Sincroniza la sesión de Supabase con (a) el resolver
 * de tokens de axios y (b) el estado Redux de auth. Reemplaza ClerkSessionSync.
 *
 * - supabase.auth maneja el refresh del access_token automáticamente.
 * - En SIGNED_IN: empuja el access_token al resolver y dispara hydrateFromClerk
 *   (que llama /api/clerk/me -> renombrar a /api/auth/me) para poblar Redux.
 * - En SIGNED_OUT: limpia token + Redux.
 *
 * Si VITE_USE_SUPABASE no está activo, este componente no hace nada.
 */
import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { supabase, isAuthEnabled, initialAuthLinkType } from '../config/supabaseClient'
import { setSessionToken, clearSessionToken } from '../utils/authToken'
import { hydrateFromClerk, clearAuth, authBootstrapped } from '../store/slices/authSlice'

export default function SupabaseSessionSync() {
  const dispatch = useDispatch()
  const hydratedRef = useRef(false)

  useEffect(() => {
    if (!isAuthEnabled() || !supabase) return
    // Bajo /demo la sesión la maneja DemoSessionBoot (token demo del server).
    // No hidratar la sesión Supabase aquí: pisaría al usuario demo y
    // TenantRoute redirigiría al tenant real del visitante logueado.
    if (window.location.pathname === '/demo' || window.location.pathname.startsWith('/demo/')) return
    let cancelled = false

    const apply = (session) => {
      if (cancelled) return
      if (session?.access_token) {
        setSessionToken(session.access_token)
        // Llegó desde un link de invitación o recuperación: antes que nada,
        // que establezca su contraseña (la sesión del link es temporal).
        if (
          (initialAuthLinkType === 'invite' || initialAuthLinkType === 'recovery') &&
          window.location.pathname !== '/establecer-password'
        ) {
          window.location.replace('/establecer-password')
          return
        }
        // Una sesión de signup confirmada aún no tiene User local. La metadata
        // editable solo orienta la navegación; el backend valida la identidad.
        const pendingCompany =
          initialAuthLinkType === 'signup' ||
          session.user?.user_metadata?.companySignup?.kind === 'company_signup_v1'
        const passwordLink = initialAuthLinkType === 'invite' || initialAuthLinkType === 'recovery'
        if (!passwordLink && pendingCompany && !session.user?.app_metadata?.tenantId) {
          hydratedRef.current = false
          if (window.location.pathname !== '/confirmar-correo') {
            window.location.replace('/confirmar-correo')
          }
          return
        }
        if (!hydratedRef.current) {
          hydratedRef.current = true
          // hydrateFromClerk.fulfilled/.rejected apaga `bootstrapping` cuando /me
          // termina. NO apagarlo antes: si se apaga mientras /me está en vuelo,
          // ProtectedRoute ve (no auth + no loading + no bootstrapping) y expulsa
          // al login pese a haber una sesión válida.
          dispatch(hydrateFromClerk())
        }
      } else {
        clearSessionToken()
        if (hydratedRef.current) {
          dispatch(clearAuth())
          hydratedRef.current = false
        }
        // Sin sesión: el chequeo inicial terminó, dejar pasar la redirección a login.
        dispatch(authBootstrapped())
      }
    }

    // Sesión inicial (al cargar la app)
    supabase.auth.getSession().then(({ data }) => apply(data.session))

    // Cambios de sesión (login, logout, refresh)
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => apply(session))

    return () => {
      cancelled = true
      sub?.subscription?.unsubscribe?.()
    }
  }, [dispatch])

  return null
}
